import { LineString } from "geojson"
import { GeometryReviewStatus } from "../utils/GeoJSONUtils"
import { FlightlineEvent } from "./FlightlineEvent"

export enum ReviewDrawerTab {
    SPLIT = "SPLIT",
    DETAILS = "DETAILS",
}

export const flightlineEventReviewId: keyof FlightlineEventReview = "fe_review_id"

export interface ReviewNode {
    id: number
    node_original_name: number
    exposure_num: number
    planned_node?: number
    flight_line_event: number
}

export enum GeometryQCStatus {
    ACCEPTED = "Accepted",
    REJECTED = "Rejected",
    CONDITIONALLY_ACCEPTED = "Conditionally Accepted",
}

export const geometryQCStatusOptions: readonly GeometryQCStatus[] = Object.values(GeometryQCStatus)

export const geometryQCStatusProperties = (
    status: GeometryQCStatus,
): { actionText: string; relatedStatus: GeometryReviewStatus } => {
    switch (status) {
        case GeometryQCStatus.ACCEPTED:
            return { actionText: "Accept Selected", relatedStatus: GeometryReviewStatus.ACCEPTED }
        case GeometryQCStatus.REJECTED:
            return { actionText: "Reject Selected", relatedStatus: GeometryReviewStatus.REFLY_REQUIRED }
        case GeometryQCStatus.CONDITIONALLY_ACCEPTED:
            return {
                actionText: "Conditionally Accept Selected",
                relatedStatus: GeometryReviewStatus.CONDITIONALLY_ACCEPTED,
            }
        default:
            throw new Error(`No action option for ${status}`)
    }
}

interface EventReviewBase {
    flight_line_event_id?: number

    id?: number
    qc_status?: GeometryQCStatus
    qc_comment?: string
}

export interface EventReviewGetDTO extends EventReviewBase {
    angle_degrees?: number

    /**
     * uses meters
     */
    shape_geojson?: LineString
    shape_length_nm?: number

    /* Frontend separates into node_info */
    start_node_id?: number /* FK to first node in range from nodes[] */
    stop_node_id?: number /* FK to last node in range from nodes[] */
    start_node_name?: number // READONLY
    stop_node_name?: number // READONLY

    /* Frontend separates into distance_info */
    start_distance_nm?: number
    stop_distance_nm?: number
}

export interface EventReviewPutDTO extends EventReviewBase {
    start_node_id?: number
    stop_node_id?: number

    start_distance_nm?: number
    stop_distance_nm?: number
}

/**
 * Frontend version
 */
export interface FlightlineEventReview extends EventReviewBase {
    fe_review_id: number

    segment_number?: number
    is_final_segment?: boolean
    flightplan_name_short_label: string
    flightplan_name_long_label: string

    /**
     * the backend only needs the node/distance fields if the review has been split
     */
    from_split_event: boolean

    /**
     * Node fields
     * - use only if there are nodes
     */
    node_info?: ReviewNodeInfo

    /**
     * Distance fields
     * - if the line event is not UL and there are no nodes, we want to use these
     */
    distance_info?: ReviewDistanceInfo

    /* FlightlineEvent from the backend */
    event: FlightlineEvent

    shape_geojson?: LineString

    /* Review data from the review */
    angle_degrees?: number
}

export interface ReviewDistanceInfo {
    start_distance_nm: number
    stop_distance_nm: number

    /**
     * Generated on the frontend
     */
    length: number
}

export interface ReviewNodeInfo {
    /** Node Array */
    node_events: ReviewNode[]

    start_node_id: number /* FK to first node in range from nodes[] */
    start_node_name: number
    stop_node_id: number /* FK to last node in range from nodes[] */
    stop_node_name: number

    /**
     * Generated on the frontend
     */
    node_count: number
}

interface MissionReviewBase {
    mission_id: number
    mission_review_id?: number
    review_date: string
    reviewer_id: string
    reviewer_notes: string
}

export interface MissionReviewPutDTO extends MissionReviewBase {
    flightline_event_reviews: EventReviewPutDTO[]
}

/**
 * Frontend version
 * - during initial creation everything is still undefined
 */
export interface MissionReviewType extends Partial<MissionReviewBase> {
    mission_name?: string
}

// Used in mission selection picker
export interface MissionReviewStatusType {
    event_ct_remaining: number
    event_ct_total: number
    mission_id: number
    mission_name: string
    project_number: string
    review_date: string
    reviewer_id: string
    reviewer_name: string
}
